import { useEffect, useState } from 'react';
import type { AgendaCategory } from '../types/Agenda';
import { getAgendaCategories } from '../api/agenda';

// Lazily-fetched, module-cached list of agenda categories — feeds the categoryId
// dropdown on the AgendaActions screen. Mirrors usePriceTypes.
let cache: AgendaCategory[] | null = null;
let inflight: Promise<AgendaCategory[]> | null = null;

function load(): Promise<AgendaCategory[]> {
  if (cache) return Promise.resolve(cache);
  if (inflight) return inflight;
  inflight = getAgendaCategories()
    .then((data) => {
      cache = data;
      inflight = null;
      return data;
    })
    .catch((err: unknown) => {
      inflight = null;
      throw err;
    });
  return inflight;
}

export function clearAgendaCategoriesCache(): void {
  cache = null;
  inflight = null;
}

interface Result {
  data: AgendaCategory[] | null;
  loading: boolean;
  error: string | null;
}

export function useAgendaCategories(): Result {
  const [state, setState] = useState<Result>(() =>
    cache
      ? { data: cache, loading: false, error: null }
      : { data: null, loading: true, error: null },
  );

  useEffect(() => {
    if (cache) {
      setState((prev) =>
        prev.data === cache ? prev : { data: cache, loading: false, error: null },
      );
      return;
    }
    let cancelled = false;
    load()
      .then((data) => {
        if (cancelled) return;
        setState({ data, loading: false, error: null });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setState({
          data: null,
          loading: false,
          error: err instanceof Error ? err.message : String(err),
        });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return state;
}
